import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Dropdown } from '../ui/Dropdown';
import { useToast } from '../../hooks/useToast';
import { app } from '../../lib/firebase';

interface AddBillingProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const AddBillingProfileModal: React.FC<AddBillingProfileModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const functions = getFunctions(app, 'europe-west1');

  const [isSaving, setIsSaving] = useState(false);
  const [newProfileData, setNewProfileData] = useState({
    profileName: '',
    apiKey: '',
    provider: 'billingo', // Default to billingo
  });

  const handleInputChange = (field: string, value: string) => {
    setNewProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    // Frontend validation
    if (!newProfileData.profileName.trim()) {
      addToast({
        type: 'error',
        message: t('settings.billing.modals.add.validation.profileNameRequired'),
      });
      return;
    }

    if (!newProfileData.apiKey.trim()) {
      addToast({
        type: 'error',
        message: t('settings.billing.modals.add.validation.apiKeyRequired'),
      });
      return;
    }

    setIsSaving(true);
    try {
      const callSaveBillingProfile = httpsCallable(functions, 'saveBillingProfile');
      const payload = {
        profileName: newProfileData.profileName,
        provider: newProfileData.provider,
        apiKey: newProfileData.apiKey,
        settings: {}, // Empty object for initial creation
      };

      await callSaveBillingProfile(payload);

      addToast({
        type: 'success',
        message: t('settings.billing.modals.add.success.profileCreatedPendingEdit'),
      });

      onSaveSuccess();
    } catch (error: any) {
      console.error('Error saving billing profile:', error);
      const errorMessage = error.message || t('settings.billing.modals.add.errors.saveFailed');
      addToast({
        type: 'error',
        message: t('settings.billing.modals.add.errors.saveFailed', { error: errorMessage }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const modalFooter = (
    <div className="flex justify-end gap-3">
      <Button variant="secondary" onClick={onClose} disabled={isSaving}>
        {t('settings.billing.modals.add.buttons.cancel')}
      </Button>
      <Button 
        variant="primary" 
        onClick={handleSave}
        isLoading={isSaving}
        disabled={isSaving}
      >
        {t('settings.billing.modals.add.buttons.save')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('settings.billing.modals.add.title')}
      footer={modalFooter}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label={t('settings.billing.modals.add.form.profileName.label')}
              value={newProfileData.profileName}
              onChange={(e) => handleInputChange('profileName', e.target.value)}
              required
            />
          </div>
          <div className="md:col-span-2">
            <Input
              label={t('settings.billing.modals.add.form.apiKey.label')}
              type="password"
              value={newProfileData.apiKey}
              onChange={(e) => handleInputChange('apiKey', e.target.value)}
              required
            />
          </div>
          <div className="md:col-span-2">
            <Dropdown
              label={t('settings.billing.modals.add.form.provider.label')}
              options={[{ value: 'billingo', label: 'Billingo' }]}
              value={newProfileData.provider}
              onChange={(value) => handleInputChange('provider', value)}
              disabled={true}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddBillingProfileModal;